import React from "react";
import { useState } from "react";
import { useContext } from "react";
import AppContext from "../../AppContext";
import { useEffect } from "react";
import { BASE_FILE_URL, BASE_URL, mapConfig } from "../../constant";
import axios from "axios";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";
import {
  GoogleMap,
  OverlayView,
  Polyline,
  Marker,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
const { CancelToken } = axios;

const AssetMarker = ({
  text,
  id,
  alarm,
  angle,
  timeStamp,
  interval,
  getTimeZoneDateTime,
  mapIconId,
  moving,
  setMoving,
}) => {
  useEffect(() => {
    if (timeStamp) {
      const sixXT = 6 * (interval * 1000);
      const tenXT = 10 * (interval * 1000);
      const localLastSensorTime = getTimeZoneDateTime(timeStamp); // Convert lastSensorTime to local time zone
      const timeDiff = new Date() - localLastSensorTime;
      if (timeDiff > tenXT) {
        setMoving(false);
      } else if (timeDiff > sixXT) {
        setMoving(true);
        const criticalTimeout = setTimeout(() => {
          setMoving(false);
        }, tenXT - timeDiff);
        return () => {
          // Clear the timeouts when the component is unmounted or when status changes
          clearTimeout(criticalTimeout);
        };
      } else {
        setMoving(true);
        const warningTimeout = setTimeout(() => {
          setMoving(true);
        }, sixXT - timeDiff); // 1 minute in milliseconds

        // Set status to critical after 2 minutes
        const criticalTimeout = setTimeout(() => {
          setMoving(false);
        }, tenXT - timeDiff);
        return () => {
          // Clear the timeouts when the component is unmounted or when status changes
          clearTimeout(warningTimeout);
          clearTimeout(criticalTimeout);
        };
      }
    }
  }, [timeStamp]);

  return (
    <div
      style={{
        transform: "translate(-19px, -30px)",
        position: "relative",
      }}
      className="h-fit w-fit "
    >
      <div
        className={`${
          alarm === "critical"
            ? "bg-red-800"
            : alarm === "warning"
            ? "bg-amber-500"
            : "bg-blue-700"
        } ${
          moving
            ? "animate-spin-slow w-7 h-7 top-[6px] left-[6px]"
            : "w-10 h-10 top-0 left-0 opacity-50"
        } absolute rounded-full`}
      ></div>
      <div
        className={`absolute left-[6px] top-[6px] h-7 w-7 ${
          !alarm ? "info" : alarm
        }-gradient flex justify-center items-center rounded-full`}
      >
        <div className="h-6 w-6 flex justify-center items-center">
          <img
            className=" h-auto self-center w-4 cursor-pointer image-full"
            alt="asset"
            id={id}
            src={
              mapIconId && !mapIconId.startsWith("0000")
                ? `${BASE_FILE_URL}/${mapIconId}`
                : "trucksvg.svg"
            }
          />
        </div>
      </div>
    </div>
  );
};

const AnyReactComponent = ({ icon }) => (
  <div
    style={{
      transform: "translate(-19px, -10px)",
      position: "relative",
    }}
    className="h-fit w-fit"
  >
    <img
      className={`rounded-full h-6 w-6 cursor-pointer`}
      alt="asset"
      src="homeStart.svg"
    />
  </div>
);
const AssetMap = forwardRef(
  ({ assetData, mode, expanded, momentData, setAutoCenter }, ref) => {
    const {
      data,
      isLoaded,
      apiLoaded,
      playbackMode,
      setPlayBackMode,
      getTimeZoneDateTime,
      darkMode,
    } = useContext(AppContext);
    const [zoomMarker, setZoomMarker] = useState(mapConfig);
    const [moving, setMoving] = useState(false);
    const { t } = useTranslation();

    const [map, setMap] = useState(null);
    const [markerIcons, setMarkerIcons] = useState({ start: null, end: null });
    const [loading, setLoading] = useState(false);
    const [liveMarkers, setLiveMarkers] = useState([]);
    const [animationInterval, setAnimationInterval] = useState();
    const [path, setPath] = useState([]);
    const [cancelToken, setCancelToken] = useState();

    const renderMarkers = (paths) => {
      const pathLines = paths.map((p) => p.gnss);
      setMapBounds(pathLines);
      animatePathWithDelay(pathLines, paths[0].timeStamp);
    };

    const animatePathWithDelay = (pathLines) => {
      const numSteps = pathLines.length;
      const delay = 1; // Delay between drawing each segment in milliseconds

      let step = 0;
      let intervalId = null;

      const drawStep = () => {
        if (playbackMode === 200 || step >= numSteps) {
          if (playbackMode !== 200)
            setMarkerIcons((pre) => ({ ...pre, end: pathLines[numSteps - 1] }));
          setLoading(false);
          step = 0;
          clearInterval(intervalId);
          setAnimationInterval(null);
          return;
        }

        const segment = pathLines[step];
        setPlayBackMode((pre) => {
          if (pre === 100) setPath((pre) => [...pre, segment]);
          return pre;
        });
        step++;
      };
      intervalId = setInterval(drawStep, delay);
      setAnimationInterval(intervalId);
    };

    const setMapBounds = (markers) => {
      setAutoCenter((pre) => {
        if (pre) {
          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((marker) => {
            bounds.extend({
              lat: marker.lat,
              lng: marker.lng,
            });
          });
          map.fitBounds(bounds);
        }
        return pre;
      });
    };

    useImperativeHandle(ref, () => ({
      clearChildState() {
        setAnimationInterval((pre) => {
          clearInterval(pre);
          return null;
        });
        clearAllMapMarkers();
        setMarkerIcons({ start: null, end: null });
      },
      setMapBounds(markers) {
        setMapBounds(markers);
      },
    }));

    useEffect(() => {
      setAutoCenter((pre) => {
        if (assetData.gnss && !loading && pre) {
          const location = {
            lat: parseFloat(assetData.gnss.latitude),
            lng: parseFloat(assetData.gnss.longitude),
            angle: parseFloat(assetData.gnss.angle),
          };
          setZoomMarker((pre) => ({ ...pre, center: location }));
          const loc = {
            gnss: {
              lat: parseFloat(assetData.gnss.latitude),
              lng: parseFloat(assetData.gnss.longitude),
              angle: parseFloat(assetData.gnss.angle),
              // altitude: parseFloat(assetData.gnss.Altitude),
            },
            timeStamp: assetData.timeStamp,
          };
          setMarkerIcons((pre) =>
            pre.end === null ? { start: null, end: loc.gnss } : pre
          );
        }
        return pre;
      });
    }, [assetData.gnss]);

    const handleApiLoaded = (map) => {
      setMap(map);
      // setMaps(maps);
    };

    const cancelRequest = () => {
      setCancelToken((pre) => {
        if (pre) {
          pre.cancel("Request canceled by user");
        }
        return pre;
      });
    };

    const getHistoryData = async () => {
      setLoading(true);
      cancelRequest();
      try {
        let cTime = new Date();
        let lastHour = new Date(cTime.getTime() - mode.map * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 23);
        const deviceId = assetData?.devices?.[0]?.device?.id;
        const source = CancelToken.source();
        if (deviceId) {
          const res = await axios.get(
            `${BASE_URL}/api/Asset/GetAssetPathCoordinates?deviceId=${deviceId}&startTime=${lastHour}&endTime=${cTime
              .toISOString()
              .slice(0, 23)}`,
            {
              cancelToken: source.token, // Pass the cancel token to the request
            }
          );
          console.log(assetData, "else");

          if (res.status === 200) {
            if (res.data.length > 0) {
              const geoLocations = res.data
                .map((d) => ({
                  gnss: {
                    lat: parseFloat(d.gnss.latitude),
                    lng: parseFloat(d.gnss.longitude),
                    altitude: parseFloat(d.gnss.altitude),
                    angle: parseFloat(d.gnss.angle),
                  },
                  timeStamp: d.timeStamp,
                }))
                .filter((x) => x !== null)
                .sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
              setPlayBackMode((pre) => {
                if (pre === 100) {
                  if (geoLocations[0].gnss.lat !== "0.0")
                    setMarkerIcons({
                      start: geoLocations[0].gnss,
                      end: null,
                    });
                  renderMarkers(geoLocations);
                }
                return pre;
              });
            } else if (assetData.gnss && assetData.gnss.latitude) {
              setLoading(false);
              const loc = {
                gnss: {
                  lat: parseFloat(assetData.gnss.latitude),
                  lng: parseFloat(assetData.gnss.longitude),
                  angle: parseFloat(assetData.gnss.angle),
                  // altitude: parseFloat(assetData.gnss.Altitude),
                },
                timeStamp: assetData.timeStamp,
              };
              setPath([loc]);
              setMarkerIcons({ start: null, end: loc.gnss });
            } else {
              setLoading(false);
            }
          } else {
            setLoading(false);
            // Handle the API response with status other than 200 (success).
            console.error("Error: Received a non-200 response from the API");
          }
        } else {
          setLoading(false);
          const loc = {
            gnss: {
              lat: parseFloat(assetData.gnss.latitude),
              lng: parseFloat(assetData.gnss.longitude),
              angle: parseFloat(assetData.gnss.angle),
              // altitude: parseFloat(assetData.gnss.Altitude),
            },
            timeStamp: assetData.timeStamp,
          };
          setPath([loc]);
          setMarkerIcons({ start: null, end: loc.gnss });
          // Handle the case where deviceId is not available.
          console.error("Error: deviceId not available");
        }
      } catch (error) {
        setLoading(false);
        // Handle any other errors that occurred during the execution.
        console.error("Error occurred:", error.message);
      }
    };

    const clearAllMapMarkers = () => {
      setPath([]);
    };

    useEffect(() => {
      clearAllMapMarkers();
      setMarkerIcons({ start: null, end: null });
      setAnimationInterval((pre) => {
        clearInterval(pre);
        return null;
      });
      setLoading(false);
      if (map && playbackMode === 100 && mode?.map !== null) getHistoryData();
    }, [map, mode.map, playbackMode]);

    const renderData = (recievedData) => {
      let value = {
        lat: parseFloat(recievedData.gnss.Latitude),
        lng: parseFloat(recievedData.gnss.Longitude),
      };
      setMarkerIcons((pre) => {
        if (pre.start === null) return { ...pre, start: value };
        else return { ...pre, end: value };
      });
      if (!loading) {
        setLiveMarkers((pre) => {
          if (pre.length > 0) {
            setPath((pre) => [...pre, ...pre.map((x) => x.gnss)]);
          }
          return [];
        });
        setPath((p) => [...p, value]);
      } else {
        setLiveMarkers((pre) => [
          ...pre,
          { gnss: value, timeStamp: recievedData.timeStamp },
        ]);
      }
    };

    const isValidData = (checkData) => {
      return (
        checkData &&
        checkData.gnss &&
        checkData.gnss.Latitude !== 0 &&
        checkData.gnss.Latitude !== "0" &&
        assetData.devices.length > 0 &&
        assetData.devices[0].device.id === checkData.deviceId
      );
    };

    useEffect(() => {
      if (
        playbackMode === 200 &&
        momentData &&
        momentData.data &&
        isValidData(momentData.data)
      ) {
        if (!momentData.dragging) renderData(momentData.data);
        else if (momentData.rangeDatas) {
          clearAllMapMarkers();
          const modifiedData = momentData.rangeDatas
            .map((element, i) => ({
              lat: parseFloat(element.gnss.Latitude),
              lng: parseFloat(element.gnss.Longitude),
              timeStamp: element.timeStamp,
              angle: parseFloat(element.gnss.Angle),
              // altitude: parseFloat(recievedData.gnss.altitude),
            }))
            .filter((x) => x.lat !== 0);
          const lastData = modifiedData[modifiedData.length - 1];
          setMarkerIcons({ start: modifiedData[0], end: lastData });
          setPath(modifiedData.map((x) => ({ lat: x.lat, lng: x.lng })));
          setMapBounds(modifiedData);
        }
      }
    }, [momentData?.data]);

    useEffect(() => {
      if (playbackMode === 100 && data && isValidData(data)) {
        renderData(data);
      }
    }, [data]);
    console.log(markerIcons);
    return (
      <div
        className={`${
          expanded === "map" ? "h-full" : "md:h-full h-[40vh]"
        } relative transition-all ease-in-out duration-150 rounded-2xl`}
      >
        <div className="h-full relative">
          {isLoaded ? (
            <GoogleMap
              center={zoomMarker.center}
              zoom={zoomMarker.zoom}
              mapContainerStyle={{
                height: "100%",
                width: "100%",
                borderRadius: "16px",
                borderColor: "transparent",
              }}
              options={{
                // styles: zoomMarker.styles,
                styles: darkMode ? zoomMarker.styles : zoomMarker.styleLight,
                fullscreenControl: false,
                streetViewControl: false,
                mapTypeControl: false,
              }}
              onLoad={handleApiLoaded}
            >
              {markerIcons.start !== null && (
                <OverlayView
                  position={{
                    lat: parseFloat(markerIcons.start.lat),
                    lng: parseFloat(markerIcons.start.lng),
                  }}
                  getPixelPositionOffset={{ x: -10, y: 0 }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <AnyReactComponent
                    lat={parseFloat(markerIcons.start.lat)}
                    lng={parseFloat(markerIcons.start.lng)}
                    key="start"
                    // icon={startIcon}
                  />
                </OverlayView>
              )}
              {markerIcons.end !== null && (
                <OverlayView
                  position={{
                    lat: parseFloat(markerIcons.end.lat),
                    lng: parseFloat(markerIcons.end.lng),
                  }}
                  getPixelPositionOffset={{ x: -10, y: 0 }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <AssetMarker
                    lat={parseFloat(markerIcons.end.lat)}
                    lng={parseFloat(markerIcons.end.lng)}
                    id="end"
                    text={
                      assetData.latestAlarm?.status === "critical"
                        ? "truckred.svg"
                        : assetData.latestAlarm?.status === "warning"
                        ? "truckyellow.svg"
                        : "truckblue.svg"
                    }
                    alarm={assetData.latestAlarm?.status}
                    angle={parseFloat(markerIcons.end.angle)}
                    interval={assetData.devices[0]?.device?.interval || 0}
                    timeStamp={assetData.timeStamp}
                    mapIconId={assetData?.mapIconId}
                    getTimeZoneDateTime={getTimeZoneDateTime}
                    moving={moving}
                    setMoving={setMoving}
                  />
                </OverlayView>
              )}

              <Polyline
                path={path}
                visible={true}
                options={{
                  strokeColor: "#0096FF",
                  strokeOpacity: 1.0,
                  strokeWeight: 2,
                }}
              />
            </GoogleMap>
          ) : null}
        </div>
        <div className="flex bg-gradient-to-r from-black w-2/5 rounded-bl-xl absolute bottom-0">
          <div className="flex-col pl-5 pr-8 border-r mt-3 mb-3 border-slate-800">
            <div className="text-xs font-semibold text-zinc-400">
              {zoomMarker.center.lat}
            </div>
            <div className="text-xxs font-medium text-neutral-500">
              {t("assetDetails.map.lat")}
            </div>
          </div>
          <div className="flex-col pl-5 pr-8 mt-3 mb-3 border-slate-800">
            <div className="text-xs font-semibold text-zinc-400">
              {zoomMarker.center.lng}
            </div>
            <div className="text-xxs font-medium text-neutral-500">
              {t("assetDetails.map.lon")}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default AssetMap;
